import {
  
  getCheeseById,
  getStyleById,
  getAllBreads as breadOptionsFn,
  
  getAllToppings as toppingOptionsFn,
  getAllDressings as dressingOptionsFn,
  getAllPreparationMethods,
} from "../db";
import { getMeatDisplayName } from "./meatUtils";
import { formatPrice } from "./currencyUtils";

/**
 * Generate a natural language order script for a sandwich
 * @param {Object} sandwich - The sandwich configuration object
 * @returns {string} The formatted order script
 */
export const generateOrderScript = (sandwich) => {
  const {
    primaryMeat,
    primaryMeatVariant,
    primaryMeatTemperature,
    secondaryMeat,
    secondaryMeatVariant,
    selectedCheeses = [],
    style: styleId = 1,
    bread: breadId,
    preparation: preparationId = 1,
    toppings: selectedToppingIds = [],
    dressings: selectedDressingIds = [],
    extraMeatQuantity = 1,
  } = sandwich;

  if (!primaryMeat) return "Select options to generate script.";

  const allToppings = toppingOptionsFn();
  const allDressings = dressingOptionsFn();
  const allBreads = breadOptionsFn();
  const allPreparations = getAllPreparationMethods();

  let scriptParts = ["I would like to order a"];
  let modificationPhrases = []; // Store individual modification phrases

  // --- Core Sandwich Description ---
  const primaryMeatName = getMeatDisplayName(primaryMeat, primaryMeatVariant);
  const temp = primaryMeatTemperature === 1 ? "hot" : "cold";
  const styleObj = getStyleById(styleId);
  const styleName = styleObj ? styleObj.name : "Plain";

  if (secondaryMeat) {
    const secondaryMeatName = getMeatDisplayName(
      secondaryMeat,
      secondaryMeatVariant
    );
    scriptParts.push(temp);
    scriptParts.push(`${primaryMeatName} and ${secondaryMeatName} combo`);
    if (styleId !== 1) scriptParts.push(styleName);
  } else {
    scriptParts.push(temp);
    scriptParts.push(primaryMeatName);
    if (styleId !== 1) scriptParts.push(styleName);
    else scriptParts.push("sandwich");
  }

  // --- Bread and Preparation ---
  const breadObj = allBreads.find((b) => b.id === parseInt(breadId));
  if (breadObj) {
    scriptParts.push(`on ${breadObj.name}`);
    const prepObj = allPreparations.find((p) => p.id === preparationId);
    if (prepObj && prepObj.id !== 1) {
      // Append directly to bread part for better flow
      scriptParts[scriptParts.length - 1] += `, ${prepObj.name.toLowerCase()}`;
    }
  }

  // --- Cheese Modifications ---
  const styleIncludesSwiss = styleId === 2 || styleId === 3; // Special or Super
  const swissCheeseId = 1; // CORRECT ID for Swiss Cheese

  if (selectedCheeses.length === 1 && selectedCheeses[0].id === 0) {
    // "No Cheese" selected
    if (styleIncludesSwiss) {
      modificationPhrases.push("no cheese");
    }
  } else if (selectedCheeses.length > 0) {
    selectedCheeses.forEach((selCheese) => {
      const cheeseObj = getCheeseById(selCheese.id);
      if (cheeseObj) {
        let text = "";
        if (selCheese.quantity === "double") text += "double ";
        if (selCheese.quantity === "triple") text += "triple ";
        text += `${cheeseObj.name} cheese`; // Explicitly add "cheese"

        // Check if it's replacing the included Swiss
        if (styleIncludesSwiss && cheeseObj.id !== swissCheeseId) {
          modificationPhrases.push(`${text} instead of Swiss`);
          // Check if it's the included Swiss but extra quantity
        } else if (
          styleIncludesSwiss &&
          cheeseObj.id === swissCheeseId &&
          selCheese.quantity !== "single"
        ) {
          modificationPhrases.push(text); // e.g., "double Swiss cheese"
          // Check if it's added to a style that doesn't include Swiss (Plain/New Yorker)
        } else if (!styleIncludesSwiss) {
          modificationPhrases.push(text);
        }
        // If styleIncludesSwiss and cheeseObj.id === swissCheeseId and quantity is single, do nothing
      }
    });
  } else if (styleIncludesSwiss) {
    // Style includes Swiss, but nothing selected implies "no cheese"
    modificationPhrases.push("no cheese");
  }
  // --- End Cheese Modifications ---

  // --- Toppings/Dressings Modifications ---
  const includedToppingIds = styleObj?.includedToppings?.map((t) => t.id) || [];
  const includedDressingIds =
    styleObj?.includedDressings?.map((d) => d.id) || [];

  const addedToppings = selectedToppingIds.filter(
    (id) => !includedToppingIds.includes(id)
  );
  const omittedToppings = includedToppingIds.filter(
    (id) => !selectedToppingIds.includes(id)
  );
  const addedDressings = selectedDressingIds.filter(
    (id) => !includedDressingIds.includes(id)
  );
  const omittedDressings = includedDressingIds.filter(
    (id) => !selectedDressingIds.includes(id)
  );

  // Temp arrays for additions/substitutions/omissions
  let subs = [];
  let adds = [];
  let omits = [];

  // Handle substitutions first
  if (omittedToppings.length === 1 && addedToppings.length === 1) {
    const omitted = allToppings.find((t) => t.id === omittedToppings[0]);
    const added = allToppings.find((t) => t.id === addedToppings[0]);
    if (omitted && added) {
      subs.push(`${added.name} instead of ${omitted.name.toLowerCase()}`);
      addedToppings.length = 0; // Clear handled items
      omittedToppings.length = 0;
    }
  }
  if (omittedDressings.length === 1 && addedDressings.length === 1) {
    const omitted = allDressings.find((d) => d.id === omittedDressings[0]);
    const added = allDressings.find((d) => d.id === addedDressings[0]);
    if (omitted && added) {
      subs.push(`${added.name} instead of ${omitted.name.toLowerCase()}`);
      addedDressings.length = 0; // Clear handled items
      omittedDressings.length = 0;
    }
  }

  // Handle remaining omissions
  omittedToppings.forEach((id) => {
    const topping = allToppings.find((t) => t.id === id);
    if (topping) omits.push(`no ${topping.name.toLowerCase()}`);
  });
  omittedDressings.forEach((id) => {
    const dressing = allDressings.find((d) => d.id === id);
    if (dressing) omits.push(`no ${dressing.name.toLowerCase()}`);
  });

  // Handle remaining additions
  addedToppings.forEach((id) => {
    const topping = allToppings.find((t) => t.id === id);
    if (topping) {
      if (topping.id === 14) adds.push(`${extraMeatQuantity}oz Extra Meat`);
      else adds.push(topping.name);
    }
  });
  addedDressings.forEach((id) => {
    const dressing = allDressings.find((d) => d.id === id);
    if (dressing) adds.push(dressing.name);
  });

  // Combine substitutions, omissions, and additions into modificationPhrases
  modificationPhrases = modificationPhrases.concat(subs, omits); // Add subs and omissions first
  if (adds.length > 0) {
    modificationPhrases.push(`add ${adds.join(", ")}`); // Prefix additions with "add"
  }
  // --- End Toppings/Dressings Modifications ---

  // --- Final Assembly ---
  let finalScript = scriptParts.join(" ");
  if (modificationPhrases.length > 0) {
    finalScript += " with "; // Always add "with" if there are any modifications
    finalScript += modificationPhrases.join(", "); // Join all mods with commas
  }

  // Clean up extra commas/spaces and add period
  finalScript = finalScript.replace(/,\s*$/g, ""); // Remove trailing comma/space
  finalScript = finalScript.replace(/\s+,/g, ","); // Space before comma
  finalScript = finalScript.replace(/,\s*,/g, ","); // Double comma
  finalScript = finalScript.replace(/with\s*,/g, "with "); // Clean up "with ,"
  finalScript = finalScript.replace(/add\s*,/g, "add "); // Clean up "add ,"
  if (!finalScript.endsWith(".")) {
    finalScript += ".";
  }
  // Final cleanup for potentially awkward phrasing like "with no cheese, add Bacon" -> "no cheese, add Bacon"
  finalScript = finalScript.replace(
    / with no cheese, add /g,
    ", no cheese, add "
  );
  finalScript = finalScript.replace(/ with no /g, ", no "); // If 'no' is the only mod after 'with'

  return finalScript;
};

/**
 * Generate a short sandwich name for display in basket
 * @param {Object} sandwich - The sandwich configuration object
 * @param {number} totalPrice - The calculated total price
 * @returns {string} The formatted sandwich name
 */
// Keep the existing generateSandwichName function as it's for display, not the order script
export const generateSandwichName = (sandwich, totalPrice) => {
  const {
    primaryMeat,
    primaryMeatVariant,
    primaryMeatTemperature,
    secondaryMeat,
    secondaryMeatVariant,
    secondaryMeatTemperature,
    selectedCheeses = [], // Default to empty array
    style,
  } = sandwich;

  if (!primaryMeat) return "Custom Sandwich";

  let sandwichName = "";

  // Get meat names
  const primaryMeatName = getMeatDisplayName(primaryMeat, primaryMeatVariant);
  const primaryTemp = primaryMeatTemperature === 1 ? "Hot " : "Cold ";

  if (secondaryMeat) {
    const secondaryMeatName = getMeatDisplayName(
      secondaryMeat,
      secondaryMeatVariant
    );
    const secondaryTemp = secondaryMeatTemperature === 1 ? "Hot " : "Cold ";

    // If temperatures are the same, display once
    if (primaryMeatTemperature === secondaryMeatTemperature) {
      sandwichName = `${primaryTemp}${primaryMeatName} & ${secondaryMeatName}`;
    } else {
      sandwichName = `${primaryTemp}${primaryMeatName} & ${secondaryTemp}${secondaryMeatName}`;
    }
  } else {
    sandwichName = `${primaryTemp}${primaryMeatName}`;
  }

  // Add style
  const styleObj = getStyleById(style);
  if (style && style !== 1 && styleObj) {
    // 1 is plain style
    sandwichName += ` ${styleObj.name}`;
  }

  // Add cheeses with quantity information
  if (
    selectedCheeses.length > 0 &&
    !(selectedCheeses.length === 1 && selectedCheeses[0].id === 0)
  ) {
    // Exclude "No Cheese"
    const hasIncludedStyle = style === 2 || style === 3; // 2 is special, 3 is super
    const swissCheeseId = 1; // Correct ID
    let cheeseTexts = [];

    selectedCheeses.forEach((selectedCheese) => {
      const cheeseDetails = getCheeseById(selectedCheese.id);

      if (cheeseDetails) {
        // Special case for included cheese (any cheese that's marked as included)
        if (hasIncludedStyle && selectedCheese.isIncluded) {
          // If the included cheese is not Swiss, note the substitution
          if (selectedCheese.id !== swissCheeseId) {
            cheeseTexts.push(`${cheeseDetails.name} (sub)`);
          }
          // If Swiss, only mention if it's double or triple
          else if (selectedCheese.quantity === "double") {
            cheeseTexts.push(`extra Swiss`);
          } else if (selectedCheese.quantity === "triple") {
            cheeseTexts.push(`triple Swiss`);
          }
          // If single quantity Swiss, no need to add anything as it's included
        } else {
          // For all other additional cheeses
          let cheeseText = `${cheeseDetails.name}`;

          if (selectedCheese.quantity === "double") {
            cheeseText += " (2x)";
          } else if (selectedCheese.quantity === "triple") {
            cheeseText += " (3x)";
          }

          cheeseTexts.push(cheeseText);
        }
      }
    });

    // Only add to sandwich name if there are cheeses to mention
    if (cheeseTexts.length > 0) {
      sandwichName += ` w/ ${cheeseTexts.join(", ")}`;
    }
  }

  // Add price to the sandwich name
  if (typeof totalPrice === "number") {
    sandwichName += ` - $${formatPrice(totalPrice)}`;
  }

  return sandwichName;
};
